import React, { useState, useRef, useEffect } from "react";

const BinaryExplorerClassic = () => {
  // --- 核心状态 ---
  const [mode, setMode] = useState("explore"); // 'explore' | 'quiz'
  const [viewMode, setViewMode] = useState("color"); // 'color' | 'binary'
  const [showHints, setShowHints] = useState(false);

  // --- 交互状态 ---
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // --- 自测模式状态 ---
  const [quizTarget, setQuizTarget] = useState(null);
  const [userGuess, setUserGuess] = useState("");
  const [quizFeedback, setQuizFeedback] = useState("idle");

  const tableContainerRef = useRef(null);

  // --- 配置数据 ---
  const columns = [
    {
      id: "F",
      label: "表 F",
      mobileLabel: "F",
      weight: 32,
      color: "bg-purple-500",
      hex: "#8b5cf6",
    },
    {
      id: "E",
      label: "表 E",
      mobileLabel: "E",
      weight: 16,
      color: "bg-blue-500",
      hex: "#3b82f6",
    },
    {
      id: "D",
      label: "表 D",
      mobileLabel: "D",
      weight: 8,
      color: "bg-green-500",
      hex: "#22c55e",
    },
    {
      id: "C",
      label: "表 C",
      mobileLabel: "C",
      weight: 4,
      color: "bg-yellow-500",
      hex: "#eab308",
    },
    {
      id: "B",
      label: "表 B",
      mobileLabel: "B",
      weight: 2,
      color: "bg-orange-500",
      hex: "#f97316",
    },
    {
      id: "A",
      label: "表 A",
      mobileLabel: "A",
      weight: 1,
      color: "bg-red-500",
      hex: "#ef4444",
    },
  ];

  const numbers = Array.from({ length: 63 }, (_, i) => i + 1);

  const hasBit = (number, weight) => (number & weight) === weight;

  // --- 逻辑处理 ---
  // 统一处理选中逻辑（兼容鼠标Hover和手机点击）
  const handleInteraction = (num) => {
    if (mode === "explore") {
      setSelectedNumber(num);
    }
  };

  const startNewQuiz = () => {
    const randomNum = Math.floor(Math.random() * 63) + 1;
    setQuizTarget(randomNum);
    setUserGuess("");
    setQuizFeedback("idle");
    setMode("quiz");
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: (randomNum - 5) * 44,
        behavior: "smooth",
      });
    }
  };

  const submitGuess = (e) => {
    e.preventDefault();
    const guess = parseInt(userGuess);
    if (isNaN(guess)) return;
    setQuizFeedback(guess === quizTarget ? "correct" : "wrong");
  };

  // --- 面板渲染内容 ---
  const renderPanelContent = () => {
    // 1. 自测模式
    if (mode === "quiz") {
      return (
        <div className="space-y-4 md:space-y-6 animate-fade-in">
          <div className="bg-indigo-50 border-2 border-indigo-100 rounded-xl p-4 md:p-5">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <h3 className="font-bold text-indigo-900 flex items-center text-sm md:text-base">
                <span className="bg-indigo-600 text-white rounded-full w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-xs mr-2">
                  ?
                </span>
                猜数字
              </h3>
              <span className="text-[10px] md:text-xs text-indigo-500 font-mono">
                TARGET HIDDEN
              </span>
            </div>

            <p className="text-xs md:text-sm text-indigo-800 mb-2 md:mb-4">
              观察左侧 <span className="font-bold text-indigo-600">高亮行</span>
              。它是几？
            </p>

            {/* 视觉提示条 */}
            <div className="flex justify-center space-x-1 mb-4 bg-white p-1 md:p-2 rounded-lg shadow-sm">
              {columns.map((col) => {
                const isActive = hasBit(quizTarget, col.weight);
                return (
                  <div
                    key={col.id}
                    className={`flex-1 h-8 md:h-10 rounded flex items-center justify-center text-xs md:text-sm font-bold transition-colors
                    ${
                      isActive
                        ? viewMode === "color"
                          ? col.color + " text-white"
                          : "bg-gray-800 text-white"
                        : "bg-gray-100 text-gray-300"
                    }`}
                  >
                    {viewMode === "binary" ? (isActive ? "1" : "0") : ""}
                  </div>
                );
              })}
            </div>

            <form onSubmit={submitGuess} className="relative">
              <input
                type="number"
                value={userGuess}
                onChange={(e) => setUserGuess(e.target.value)}
                placeholder="?"
                className="w-full text-center text-2xl md:text-3xl font-bold py-2 md:py-4 border-2 border-indigo-200 rounded-xl focus:border-indigo-500 outline-none transition-all"
                autoFocus={false} // 手机端防止自动弹键盘遮挡
                disabled={quizFeedback === "correct"}
              />
              {quizFeedback === "idle" && (
                <button
                  type="submit"
                  className="absolute right-2 top-1.5 bottom-1.5 md:top-2 md:bottom-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 md:px-4 rounded-lg font-medium transition-colors text-sm md:text-base"
                >
                  确认
                </button>
              )}
            </form>
          </div>

          {quizFeedback === "correct" && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 md:p-4 text-center animate-bounce-in">
              <div className="text-2xl md:text-4xl mb-1 md:mb-2">🎉</div>
              <div className="text-green-800 font-bold text-base md:text-lg">
                答对了！是 {quizTarget}
              </div>
              <button
                onClick={startNewQuiz}
                className="mt-2 md:mt-4 w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition-colors text-sm md:text-base"
              >
                下一题 →
              </button>
            </div>
          )}

          {quizFeedback === "wrong" && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2 md:p-4 text-center animate-shake">
              <div className="text-red-800 font-bold text-sm md:text-base">
                ❌ 算错了，再试试？
              </div>
            </div>
          )}
        </div>
      );
    }

    // 2. 探究模式 - 空闲
    if (!selectedNumber) {
      return (
        <div className="h-full flex flex-row md:flex-col items-center justify-center text-gray-400 p-2 md:p-6 text-center gap-2 md:gap-4">
          <div className="w-10 h-10 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center text-xl md:text-3xl">
            👆
          </div>
          <div className="text-left md:text-center">
            <h3 className="font-bold text-gray-600 text-sm md:text-base">
              暂无数据
            </h3>
            <p className="text-xs md:text-sm mt-1">点击或悬停左侧表格</p>
          </div>
        </div>
      );
    }

    // 3. 探究模式 - 未开启原理
    if (!showHints) {
      return (
        <div className="animate-fade-in text-center h-full flex flex-col justify-center md:block pt-2 md:pt-10">
          <div className="inline-block p-2 md:p-6 rounded-full bg-gray-50 mb-2 md:mb-6 shadow-inner">
            <span className="text-4xl md:text-6xl font-bold text-gray-800">
              {selectedNumber}
            </span>
          </div>
          <p className="text-gray-600 font-medium mb-2 text-sm md:text-base">
            当前选中数字
          </p>
          <p className="text-xs md:text-sm text-gray-400 mb-4 md:mb-8 leading-relaxed">
            观察色块分布规律
          </p>
          <button
            onClick={() => setShowHints(true)}
            className="w-full py-2 md:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg md:rounded-xl font-bold shadow-lg transition-all text-sm md:text-base"
          >
            🔓 揭示原理
          </button>
        </div>
      );
    }

    // 4. 探究模式 - 开启原理
    const activeCols = columns.filter((col) =>
      hasBit(selectedNumber, col.weight)
    );

    return (
      <div className="animate-fade-in h-full overflow-y-auto">
        {/* 算式区 */}
        <div className="bg-white rounded-lg md:rounded-xl border border-gray-200 shadow-sm p-2 md:p-5 mb-2 md:mb-6">
          <div className="flex flex-wrap items-baseline justify-center gap-1 md:gap-2 mb-1 md:mb-2">
            <span className="text-2xl md:text-4xl font-bold text-gray-800">
              {selectedNumber}
            </span>
            <span className="text-xl md:text-2xl text-gray-400">=</span>
            {activeCols.length === 0 ? (
              <span className="text-gray-400 italic">0</span>
            ) : (
              activeCols.map((col, idx) => (
                <div key={col.id} className="flex items-center">
                  <span
                    className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded font-mono font-bold text-white text-sm md:text-base ${col.color}`}
                  >
                    {col.weight}
                  </span>
                  {idx < activeCols.length - 1 && (
                    <span className="mx-0.5 md:mx-1 text-gray-400">+</span>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="text-center text-[10px] md:text-xs text-gray-400 mt-1 md:mt-2">
            二进制位权加法分解
          </div>
        </div>

        {/* 01 编码展示 */}
        <div className="space-y-1 md:space-y-2 mb-2 md:mb-6">
          <h4 className="hidden md:block text-xs font-bold text-gray-400 uppercase tracking-wider">
            Binary Code
          </h4>
          <div className="grid grid-cols-6 gap-px md:gap-1 bg-gray-800 p-1 md:p-3 rounded md:rounded-lg">
            {columns.map((col) => {
              const isActive = hasBit(selectedNumber, col.weight);
              return (
                <div
                  key={col.id}
                  className="flex flex-col items-center text-center"
                >
                  <span
                    className={`font-mono text-sm md:text-lg font-bold ${
                      isActive ? "text-white" : "text-gray-600"
                    }`}
                  >
                    {isActive ? "1" : "0"}
                  </span>
                  <span className="hidden md:block text-[9px] text-gray-500 mt-1">
                    {col.weight}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="hidden md:block text-sm text-gray-600 leading-relaxed bg-blue-50 p-4 rounded-lg border border-blue-100">
          <p>
            将这些“点亮”表格对应的
            <span className="font-bold text-blue-800">位权</span>
            相加，即得到原数字。
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 text-gray-800 font-sans overflow-hidden relative">
      {/* --- 顶部导航栏 --- */}
      <header className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0 z-30 px-3 py-2 md:px-6 md:py-3 flex justify-between items-center">
        <div className="flex items-center gap-2 md:gap-6">
          <div className="flex flex-col">
            <h1 className="text-sm md:text-lg font-extrabold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent whitespace-nowrap">
              Binary Reader
            </h1>
            <span className="hidden md:block text-xs text-gray-400">
              数学原理可视化探究
            </span>
          </div>

          <div className="bg-gray-100 p-0.5 md:p-1 rounded-lg flex text-[10px] md:text-sm">
            <button
              onClick={() => setMode("explore")}
              className={`px-2 md:px-3 py-1 md:py-1.5 rounded md:rounded-md transition-all font-medium ${
                mode === "explore"
                  ? "bg-white shadow text-blue-700"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              探究
            </button>
            <button
              onClick={startNewQuiz}
              className={`px-2 md:px-3 py-1 md:py-1.5 rounded md:rounded-md transition-all font-medium ${
                mode === "quiz"
                  ? "bg-white shadow text-indigo-700"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              自测
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5 md:p-1">
            <button
              onClick={() => setViewMode("color")}
              className={`px-2 md:px-3 py-1 rounded flex items-center gap-1 text-[10px] md:text-sm transition-colors ${
                viewMode === "color"
                  ? "bg-blue-50 text-blue-600 font-bold"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span className="md:hidden">色</span>
              <span className="hidden md:inline">🎨 染色</span>
            </button>
            <div className="w-px h-3 md:h-4 bg-gray-200 mx-0.5"></div>
            <button
              onClick={() => setViewMode("binary")}
              className={`px-2 md:px-3 py-1 rounded flex items-center gap-1 text-[10px] md:text-sm transition-colors ${
                viewMode === "binary"
                  ? "bg-gray-800 text-white font-bold"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <span className="md:hidden">01</span>
              <span className="hidden md:inline">🔢 01</span>
            </button>
          </div>

          {/* 原理开关 - 手机端为按钮，电脑端为Switch */}
          {mode === "explore" && (
            <>
              <button
                onClick={() => setShowHints(!showHints)}
                className={`md:hidden px-2 py-1 text-[10px] border rounded ${
                  showHints
                    ? "bg-green-50 border-green-200 text-green-700"
                    : "bg-white border-gray-200 text-gray-500"
                }`}
              >
                {showHints ? "原理开" : "原理关"}
              </button>

              <label className="hidden md:flex items-center space-x-2 cursor-pointer select-none px-2 py-2 hover:bg-gray-50 rounded-lg transition-colors group">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={showHints}
                    onChange={() => setShowHints(!showHints)}
                  />
                  <div
                    className={`block w-9 h-5 rounded-full transition-colors shadow-inner ${
                      showHints ? "bg-green-500" : "bg-gray-300"
                    }`}
                  ></div>
                  <div
                    className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform shadow ${
                      showHints ? "transform translate-x-4" : ""
                    }`}
                  ></div>
                </div>
                <span
                  className={`text-sm font-medium ${
                    showHints ? "text-green-700" : "text-gray-500"
                  }`}
                >
                  显示原理
                </span>
              </label>
            </>
          )}
        </div>
      </header>

      {/* --- 主布局：桌面端维持 flex-row (左右)，手机端切换 flex-col (上下) --- */}
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* 左侧：表格区域 */}
        <div
          ref={tableContainerRef}
          className="flex-1 overflow-y-auto bg-white relative scroll-smooth w-full"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          onMouseLeave={() => setIsDragging(false)}
          onMouseUp={() => setIsDragging(false)}
        >
          {/* 内边距调整：电脑端 p-4, 手机端 p-2 且底部留白防遮挡 */}
          <div className="p-2 md:p-4 pb-40 md:pb-24 w-full">
            <table className="w-full table-fixed border-separate border-spacing-y-1">
              <thead className="sticky top-0 z-10">
                <tr>
                  {/* 角标列 */}
                  <th className="bg-white p-1 md:p-2 w-8 md:w-16 border-b-2 border-gray-100">
                    <span className="text-[10px] md:text-xs font-bold text-gray-300">
                      #
                    </span>
                  </th>
                  {/* 动态表头 */}
                  {columns.map((col) => (
                    <th
                      key={col.id}
                      className="bg-white p-1 md:p-2 border-b-2 border-gray-100"
                    >
                      <div className="flex flex-col items-center transition-all duration-300">
                        <span
                          className={`px-1 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-sm font-bold border whitespace-nowrap`}
                          style={{
                            backgroundColor: `${col.hex}15`,
                            color: col.hex,
                            borderColor: `${col.hex}30`,
                          }}
                        >
                          {/* 手机只显A，电脑显表A */}
                          <span className="md:hidden">{col.mobileLabel}</span>
                          <span className="hidden md:inline">{col.label}</span>
                        </span>

                        <div
                          className={`transition-all duration-500 ${
                            showHints
                              ? "max-h-8 opacity-100 mt-1"
                              : "max-h-0 opacity-0"
                          } overflow-hidden`}
                        >
                          <span className="text-[9px] md:text-xs font-bold text-gray-600">
                            {col.weight}
                          </span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="text-sm md:text-base">
                {numbers.map((num) => {
                  const isSelected = selectedNumber === num;
                  const isQuizTarget = mode === "quiz" && quizTarget === num;
                  const isDimmed = mode === "quiz" && !isQuizTarget;

                  return (
                    <tr
                      key={num}
                      className={`group transition-all duration-100 rounded-lg
                        ${
                          isSelected && mode === "explore"
                            ? "bg-gray-50 md:shadow-sm md:scale-[1.01] relative z-10"
                            : ""
                        }
                        ${isDimmed ? "opacity-20 blur-[1px]" : "opacity-100"}
                      `}
                      // 关键：点击兼容手机，移入兼容电脑
                      onClick={() => handleInteraction(num)}
                      onMouseDown={() => {
                        if (mode === "explore") setIsDragging(true);
                      }}
                      onMouseEnter={() => {
                        if (mode === "explore" && isDragging)
                          handleInteraction(num);
                        else if (mode === "explore") handleInteraction(num);
                      }}
                    >
                      <td className="text-center py-1 md:py-2 rounded-l-lg cursor-pointer select-none">
                        <div
                          className={`
                           inline-flex w-6 h-6 md:w-8 md:h-8 items-center justify-center rounded-full font-bold text-xs md:text-sm transition-colors
                           ${
                             isSelected || isQuizTarget
                               ? "bg-gray-800 text-white"
                               : "text-gray-400 group-hover:text-gray-600"
                           }
                        `}
                        >
                          {mode === "quiz" && isQuizTarget ? "?" : num}
                        </div>
                      </td>

                      {columns.map((col) => {
                        const isActive = hasBit(num, col.weight);
                        return (
                          <td
                            key={col.id}
                            className="p-0.5 md:p-1 text-center h-10 md:h-12 first:rounded-l-lg last:rounded-r-lg"
                          >
                            <div
                              className={`
                                w-full h-full rounded flex items-center justify-center transition-all duration-200 border border-transparent
                                ${
                                  isActive
                                    ? viewMode === "color"
                                      ? col.color
                                      : "bg-gray-800"
                                    : "bg-transparent"
                                }
                                ${
                                  viewMode === "binary" && !isActive
                                    ? "bg-gray-100"
                                    : ""
                                }
                                ${
                                  isSelected &&
                                  mode === "explore" &&
                                  isActive &&
                                  viewMode === "color"
                                    ? "scale-95"
                                    : ""
                                }
                              `}
                            >
                              {viewMode === "binary" && (
                                <span
                                  className={`font-mono text-xs md:text-lg font-bold ${
                                    isActive ? "text-white" : "text-gray-300"
                                  }`}
                                >
                                  {isActive ? "1" : "0"}
                                </span>
                              )}

                              {viewMode === "color" &&
                                isActive &&
                                showHints &&
                                isSelected &&
                                mode === "explore" && (
                                  <span className="text-white/90 text-xs font-bold animate-fade-in hidden md:block">
                                    {col.weight}
                                  </span>
                                )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 右侧：固定面板 */}
        {/* 桌面端 (md): w-80, flex-shrink-0, static, 左边框
            手机端 (default): fixed bottom-0, w-full, 上阴影, z-40
        */}
        <div
          className={`
          bg-white flex flex-col z-40
          fixed bottom-0 left-0 right-0 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] rounded-t-2xl h-auto max-h-[40vh]
          md:static md:w-80 md:h-full md:shadow-[-4px_0_20px_rgba(0,0,0,0.03)] md:rounded-none md:border-l md:border-gray-200 md:max-h-none flex-shrink-0
        `}
        >
          {/* 只有电脑端显示标题栏，手机端直接显示内容省空间 */}
          <div className="hidden md:block p-4 md:p-6 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center">
              {mode === "quiz" ? "🧠 挑战模式" : "📐 原理解析"}
              {mode === "explore" && showHints && (
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  On
                </span>
              )}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {renderPanelContent()}
          </div>

          <div className="hidden md:block p-4 border-t border-gray-100 text-center text-xs text-gray-400">
            © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师
            保留所有权利。
          </div>
        </div>
      </main>

      <style jsx>{`
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-bounce-in {
          animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        .animate-shake {
          animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
          }
        }
        @keyframes shake {
          10%,
          90% {
            transform: translateX(-1px);
          }
          20%,
          80% {
            transform: translateX(2px);
          }
          30%,
          50%,
          70% {
            transform: translateX(-4px);
          }
          40%,
          60% {
            transform: translateX(4px);
          }
        }
      `}</style>
    </div>
  );
};

export default BinaryExplorerClassic;
